import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Plus, Loader2, ShieldAlert, ShieldCheck, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { startRegistration } from '@simplewebauthn/browser';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { doorApi } from '@/api/door';
import { CredentialsList } from './CredentialsList';
import { MyAccessLog } from './MyAccessLog';
import { useDoorOpen } from './hooks/useDoorOpen';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const resp = (error as any).response?.data;
    return resp?.message || resp?.error || 'Неизвестная ошибка';
  }
  if (error instanceof Error) return error.message;
  return 'Неизвестная ошибка';
}

export function DoorPage() {
  const queryClient = useQueryClient();
  const { openDoor: handleOpenDoor, isOpening } = useDoorOpen();
  const [isRegistering, setIsRegistering] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['door', 'status'],
    queryFn: doorApi.getStatus,
    staleTime: 0,
  });

  const { data: credentials = [], isLoading: credsLoading } = useQuery({
    queryKey: ['door', 'credentials'],
    queryFn: doorApi.getCredentials,
    enabled: !!status?.canAccessOffice,
  });

  const archiveMutation = useMutation({
    mutationFn: doorApi.archiveCredential,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['door'] });
      toast.success('Устройство удалено');
    },
    onError: (error) => {
      toast.error('Ошибка', { description: getErrorMessage(error) });
    },
  });

  const hasApproved = credentials.some((c) => c.status === 'approved');

  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      const options = await doorApi.getRegistrationOptions();
      const credential = await startRegistration({ optionsJSON: options });

      // Try to get device name from user agent
      const ua = navigator.userAgent;
      let deviceName: string | undefined;
      if (/iPhone/.test(ua)) deviceName = 'iPhone';
      else if (/iPad/.test(ua)) deviceName = 'iPad';
      else if (/Android/.test(ua)) {
        const match = ua.match(/;\s*([^;)]+)\s*Build/);
        deviceName = match?.[1]?.trim() || 'Android';
      }

      await doorApi.registerCredential(credential, deviceName);
      queryClient.invalidateQueries({ queryKey: ['door'] });
      toast.success('Устройство зарегистрировано', {
        description: 'Ожидает одобрения администратора',
      });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        toast.error('Регистрация отменена');
      } else {
        toast.error('Ошибка регистрации', { description: getErrorMessage(error) });
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const isLoading = statusLoading || credsLoading;

  return (
    <div className="min-h-screen bg-background">
      <Header title="Дверь" />

      <main className="p-4 space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !status?.canAccessOffice ? (
          /* State 1: No access */
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <ShieldAlert className="w-16 h-16 text-muted-foreground" />
            <div>
              <p className="text-lg font-medium">Доступ в офис не разрешён</p>
              <p className="text-sm text-muted-foreground mt-1">
                Обратитесь к администратору
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Open Door Button or Not in Network message */}
            {hasApproved && status?.isInTrustedNetwork && (
              <div className="flex flex-col items-center gap-4 py-8">
                <button
                  onClick={handleOpenDoor}
                  disabled={isOpening}
                  className="w-40 h-40 rounded-full bg-primary text-primary-foreground flex flex-col items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform disabled:opacity-50 disabled:active:scale-100"
                >
                  {isOpening ? (
                    <Loader2 className="w-12 h-12 animate-spin" />
                  ) : (
                    <DoorOpen className="w-12 h-12" />
                  )}
                  <span className="text-sm font-medium">
                    {isOpening ? 'Проверка...' : 'Открыть дверь'}
                  </span>
                </button>
              </div>
            )}

            {hasApproved && !status?.isInTrustedNetwork && (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <WifiOff className="w-16 h-16 text-muted-foreground" />
                <div>
                  <p className="text-lg font-medium">Вы не в офисной сети</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Подключитесь к офисному Wi-Fi для открытия двери
                  </p>
                </div>
              </div>
            )}

            {/* Register prompt */}
            {!hasApproved && (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <ShieldCheck className="w-16 h-16 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Зарегистрируйте устройство для доступа
                </p>
              </div>
            )}

            <Button
              onClick={handleRegister}
              disabled={isRegistering}
              variant={hasApproved ? 'outline' : 'default'}
              className="w-full"
            >
              {isRegistering ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Зарегистрировать устройство
            </Button>

            {/* Tabs: History and Credentials */}
            <Tabs defaultValue="history" className="mt-6">
              <TabsList className="w-full">
                <TabsTrigger value="history" className="flex-1">История</TabsTrigger>
                <TabsTrigger value="keys" className="flex-1">Мои ключи</TabsTrigger>
              </TabsList>
              <TabsContent value="history">
                <MyAccessLog />
              </TabsContent>
              <TabsContent value="keys">
                <CredentialsList
                  credentials={credentials}
                  onArchive={(id) => archiveMutation.mutate(id)}
                  isArchiving={archiveMutation.isPending}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
